import React from 'react';
import { motion } from 'framer-motion';
import WRCShowcase from '../components/WRCShowcase';

const Apps: React.FC = () => {
  return (
    <div className="py-16 bg-black relative overflow-hidden">
      <motion.div 
        className="absolute inset-0 bg-gradient-to-b from-gold-950/30 to-black/90"
        animate={{ 
          backgroundPosition: ['center top', 'center bottom', 'center top'],
        }}
        transition={{ 
          duration: 25, 
          repeat: Infinity, 
          ease: 'linear' 
        }}
        style={{ 
          backgroundSize: 'auto 200%',
        }}
      />
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <motion.div 
          className="max-w-4xl mx-auto text-center mb-12"
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <h1 className="text-4xl md:text-5xl font-bold text-gold-400 mb-6">Apps & Products</h1>
          <p className="text-gray-300 text-lg max-w-3xl mx-auto">Custom apps we've built for clients, plus the products Setora ships itself. From WRC Now's Concrete Pro — a full operating system for residential concrete contractors — to PlayMaker Pics, our AI-powered sports photography platform.</p>
        </motion.div>

        <WRCShowcase />

        <motion.div
          className="bg-gray-900/80 border border-gold-800/30 rounded-3xl overflow-hidden shadow-2xl shadow-gold-900/30 backdrop-blur-sm mb-12"
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          whileHover={{ y: -5, boxShadow: '0 15px 40px -20px rgba(211, 158, 61, 0.4)' }}
        >
          <div className="h-64 bg-gray-800 relative overflow-hidden">
            <motion.img 
              src="/setora-logo-v2.png" 
              alt="PlayMaker Pics" 
              className="w-full h-full object-cover"
              initial={{ scale: 1.2 }}
              animate={{ scale: 1 }}
              transition={{ duration: 2, ease: 'easeOut', delay: 0.5 }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-gray-900/80 to-transparent" />
          </div>
          <div className="p-8">
            {/* PlayMaker Pics Logo Section */}
            <div className="flex items-center justify-center mb-6">
              <motion.div 
                className="w-20 h-20 bg-gold-400 rounded-2xl flex items-center justify-center shadow-lg shadow-gold-500/30"
                initial={{ scale: 0, rotate: -180 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{ duration: 0.8, delay: 0.3, type: "spring", stiffness: 200 }}
              >
                <span className="text-black font-bold text-2xl">PP</span>
              </motion.div>
              <div className="ml-4">
                <h2 className="text-3xl font-bold text-gold-400">PlayMaker Pics</h2>
                <p className="text-gray-400 text-sm">AI-Powered Sports Photography</p>
              </div>
            </div>
            
            <p className="text-gray-300 text-base mb-6 max-w-3xl">PlayMaker Pics is revolutionizing sports photography with AI technology that instantly transforms amateur photos into professional, branded images. Perfect for youth sports events, corporate tournaments, and professional games, our app delivers studio-quality results at the click of a button. With a rapidly growing user base and proven market traction, PlayMaker Pics is just the beginning of Setora's vision to redefine creative content.</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div className="bg-gold-900/30 border border-gold-800/50 rounded-2xl p-4">
                <h3 className="text-gold-400 font-semibold mb-2">Instant Branding</h3>
                <p className="text-gray-300 text-sm">Apply custom overlays, logos, and themes to photos in real-time, ensuring every image aligns with your brand identity.</p>
              </div>
              <div className="bg-gold-900/30 border border-gold-800/50 rounded-2xl p-4">
                <h3 className="text-gold-400 font-semibold mb-2">On-Site Printing</h3>
                <p className="text-gray-300 text-sm">Offer immediate photo printing at events, creating tangible keepsakes that enhance customer engagement.</p>
              </div>
              <div className="bg-gold-900/30 border border-gold-800/50 rounded-2xl p-4">
                <h3 className="text-gold-400 font-semibold mb-2">Scalable Solution</h3>
                <p className="text-gray-300 text-sm">From local games to national tournaments, our cloud-based platform scales effortlessly to meet demand.</p>
              </div>
            </div>
            <motion.a 
              href="https://playmakerpics.com" 
              className="inline-block px-6 py-3 bg-gold-400 text-black font-semibold rounded-xl shadow-lg shadow-gold-500/30 transition-all duration-300 hover:bg-gold-300 pulse-glow"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Explore PlayMaker Pics
            </motion.a>
          </div>
        </motion.div>

        <motion.div
          className="bg-gray-900/80 border border-gold-800/30 rounded-3xl p-8 md:p-10 text-center shadow-xl shadow-gold-900/20 backdrop-blur-sm mb-16"
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
        >
          <h2 className="text-2xl md:text-3xl font-bold text-gold-400 mb-3">Need an app like this for your business?</h2>
          <p className="text-gray-300 max-w-2xl mx-auto mb-6">
            PlayMaker Pics is one example of what we build. The same team designs and ships custom web apps, mobile apps, and AI tools for businesses across every industry — see our work or tell us what you need.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="/#work"
              className="inline-block px-6 py-3 bg-gold-400/20 hover:bg-gold-400/30 text-gold-300 rounded-xl transition-all duration-300 border border-gold-700/30 hover:border-gold-600/50"
            >
              See Our Work
            </a>
            <motion.a
              href="/contact"
              className="inline-block px-6 py-3 bg-gradient-to-r from-gold-500 to-gold-600 hover:from-gold-400 hover:to-gold-500 text-black font-semibold rounded-xl shadow-lg shadow-gold-500/30 transition-all duration-300 pulse-glow"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Start a Project
            </motion.a>
          </div>
        </motion.div>
      </div>
      <motion.div 
        className="absolute top-0 left-0 w-96 h-96 bg-gold-500/5 rounded-full blur-3xl"
        animate={{ 
          x: [0, -30, 0],
          y: [0, 30, 0],
        }}
        transition={{ 
          duration: 20,
          repeat: Infinity,
          ease: 'easeInOut'
        }}
      />
    </div>
  );
};

export default Apps; 